import { WELCOME_ICON_KEYS, type WelcomeIconKey } from '@utazzo/shared';
import { UzButton, UzInput, UzSelect, UzSwitch, UzTextarea, cn } from '@utazzo/ui';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import React from 'react';

import { WelcomeIcon } from './WelcomeIcon';

/** Small building blocks for the Welcome page editor forms. */

export const TextField = ({
  label,
  value,
  onChange,
  max,
  placeholder,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  placeholder?: string;
  type?: string;
}): React.JSX.Element => (
  <UzInput
    label={label}
    value={value}
    maxLength={max}
    placeholder={placeholder}
    type={type}
    onChange={(e) => onChange(e.target.value)}
  />
);

export const AreaField = ({
  label,
  value,
  onChange,
  max,
  rows = 3,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  max: number;
  rows?: number;
  hint?: string;
}): React.JSX.Element => (
  <div>
    <UzTextarea
      label={label}
      value={value}
      maxLength={max}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
    />
    <p className="mt-1 flex justify-between text-xs text-[var(--fg-faint)]">
      <span>{hint}</span>
      <span>
        {value.length}/{max}
      </span>
    </p>
  </div>
);

export const VisibleSwitch = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}): React.JSX.Element => (
  <UzSwitch
    checked={checked}
    onCheckedChange={onChange}
    label="Show this section"
    description="Hidden sections disappear from everyone’s page (content is kept)."
  />
);

const ICON_OPTIONS = WELCOME_ICON_KEYS.map((k) => ({
  value: k,
  label: k.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
}));

export const IconSelect = ({
  value,
  onChange,
}: {
  value: WelcomeIconKey;
  onChange: (v: WelcomeIconKey) => void;
}): React.JSX.Element => (
  <div className="flex items-end gap-2">
    <span className="mb-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E0F9FC] text-[#093F51]">
      <WelcomeIcon name={value} className="h-5 w-5" />
    </span>
    <UzSelect<WelcomeIconKey>
      label="Icon"
      value={value}
      onValueChange={onChange}
      options={ICON_OPTIONS}
      containerClassName="flex-1"
    />
  </div>
);

/**
 * Ordered list editor: one bordered card per item with move / delete, and an
 * "Add" button until `max` is reached.
 */
export function ListEditor<T>({
  items,
  onChange,
  renderItem,
  newItem,
  max,
  addLabel,
  itemLabel,
}: {
  items: T[];
  onChange: (next: T[]) => void;
  renderItem: (item: T, update: (patch: Partial<T>) => void, index: number) => React.ReactNode;
  newItem: () => T;
  max: number;
  addLabel: string;
  itemLabel: (item: T, index: number) => string;
}): React.JSX.Element {
  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    if (it !== undefined) next.splice(to, 0, it);
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div
          key={i}
          className="rounded-[var(--r-md)] border border-[var(--ds-border)] bg-[var(--bg)] p-3"
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="min-w-0 truncate text-sm font-semibold text-[var(--fg)]">
              {i + 1}. {itemLabel(item, i) || 'Untitled'}
            </p>
            <div className="flex shrink-0 gap-1">
              <UzButton
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                tooltip="Move up"
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
              >
                <ArrowUp className="h-4 w-4" />
              </UzButton>
              <UzButton
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                tooltip="Move down"
                disabled={i === items.length - 1}
                onClick={() => move(i, i + 1)}
              >
                <ArrowDown className="h-4 w-4" />
              </UzButton>
              <UzButton
                type="button"
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-[var(--danger,#dc2626)]"
                tooltip="Remove"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
              >
                <Trash2 className="h-4 w-4" />
              </UzButton>
            </div>
          </div>
          <div className="grid gap-3">
            {renderItem(
              item,
              (patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x))),
              i,
            )}
          </div>
        </div>
      ))}
      {items.length < max ? (
        <UzButton
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => onChange([...items, newItem()])}
        >
          {addLabel}
        </UzButton>
      ) : (
        <p className="text-xs text-[var(--fg-faint)]">Maximum of {max} reached.</p>
      )}
    </div>
  );
}

export const FieldGrid = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element => (
  <div className={cn('grid gap-3 sm:grid-cols-2', className)}>{children}</div>
);
